import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Paper,
  Tooltip,
  Badge
} from '@mui/material';
import {
  AccessTime,
  CalendarToday,
  ChevronLeft,
  ChevronRight,
  Close,
  Event,
  Assignment,
  School,
  VolunteerActivism,
  Today,
  EventNote
} from '@mui/icons-material';

// Updated imports to match profile page structure
import axiosInstance from "../../../utils/axios/axiosInstance";
import API from "../../../config/apiconfig";
import { toast } from 'react-toastify';
import { useDispatch } from "react-redux";
import { userApiLoader } from "../../../action/loaderActions";

// Loading gif import (update path as needed)
import loadingGif from '../../../assets/images/loading.gif';

const Planner = () => {
  const dispatch = useDispatch();
  
  // Use ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false); // Track if API call is in progress
  
  // State declarations
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true); // Start as true for initial load
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateData, setSelectedDateData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Calendar navigation state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth()); // 0-based month

  // Helper function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Helper function to get month names - memoized to prevent unnecessary re-renders
  const getMonthName = useCallback((monthIndex) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  }, []);

  // Helper function to get type info with colors and icons
  const getTypeInfo = useCallback((type) => {
    switch (type) {
      case 'event':
        return { 
          label: 'Event', 
          color: '#2196f3', 
          bgColor: '#e3f2fd', 
          icon: <Event sx={{ fontSize: 14 }} />,
          lightColor: 'rgba(33, 150, 243, 0.1)',
          dotColor: '#2196f3'
        };
      case 'todo':
        return { 
          label: 'Todo', 
          color: '#ff9800', 
          bgColor: '#fff3e0', 
          icon: <Assignment sx={{ fontSize: 14 }} />,
          lightColor: 'rgba(255, 152, 0, 0.1)',
          dotColor: '#ff9800'
        };
      case 'class':
        return { 
          label: 'Class', 
          color: '#4caf50', 
          bgColor: '#e8f5e9', 
          icon: <School sx={{ fontSize: 14 }} />,
          lightColor: 'rgba(76, 175, 80, 0.1)',
          dotColor: '#4caf50'
        };
      case 'extra_class':
        return { 
          label: 'Extra Class', 
          color: '#9c27b0', 
          bgColor: '#f3e5f5', 
          icon: <School sx={{ fontSize: 14 }} />,
          lightColor: 'rgba(156, 39, 176, 0.1)',
          dotColor: '#9c27b0'
        };
      case 'opportunity':
        return { 
          label: 'Opportunity', 
          color: '#f44336', 
          bgColor: '#ffebee', 
          icon: <VolunteerActivism sx={{ fontSize: 14 }} />,
          lightColor: 'rgba(244, 67, 54, 0.1)',
          dotColor: '#f44336'
        };
      default:
        return { 
          label: 'Other', 
          color: '#795548', 
          bgColor: '#efebe9', 
          icon: <EventNote sx={{ fontSize: 14 }} />,
          lightColor: 'rgba(121, 85, 72, 0.1)',
          dotColor: '#795548'
        };
    }
  }, []);

  // Helper function to format date
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'No date';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }, []);

  // Updated fetch calendar data function to match profile page API structure
  const fetchCalendarData = useCallback(async (month, year) => {
    if (!isMountedRef.current || isLoadingRef.current) return; // Prevent multiple concurrent calls

    try {
      // Set loading flags
      isLoadingRef.current = true;
      setLoading(true);
      
      // Use dispatch for loading state like in profile page
      dispatch(userApiLoader({isApiLoader: true}));
      setError(null);

      console.log(`📅 Fetching calendar data for ${month}/${year}`);

      // Updated API call to match profile page structure
      const response = await axiosInstance.get(
        `${API.USER_MANAGEMENT_API_BASE_URL}${API.CALENDAR_EVENTS}?month=${month}&year=${year}`
      );

      if (!isMountedRef.current) return;

      console.log('📥 Calendar API Response:', response.data);

      // Handle response similar to profile page
      if (response.data.status === 200 || response.data) {
        const data = response.data.data || response.data || [];
        console.log(`📊 Fetched ${data.length} calendar items`);
        setCalendarData(data);

        if (data.length === 0) {
          const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
          ];
          toast.info(`No events found for ${monthNames[month - 1]} ${year}`, {
            autoClose: 3000,
          });
        }
      } else {
        throw new Error(response.data.message || 'Failed to fetch calendar data');
      }
    } catch (err) {
      console.error('❌ Error fetching calendar data:', err);

      let errorMessage = 'Failed to fetch calendar data';

      // Error handling similar to profile page
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (isMountedRef.current) {
        setError(errorMessage);
        toast.error(errorMessage, {
          autoClose: 6000,
        });
      }
    } finally {
      if (isMountedRef.current) {
        // Use dispatch for loading state like in profile page
        dispatch(userApiLoader({isApiLoader: false}));
        setLoading(false);
        isLoadingRef.current = false;
      }
    }
  }, [dispatch]); // Only dispatch as dependency

  // Get data for a specific date
  const getDataForDate = useCallback((date) => {
    const dateString = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
    return calendarData.filter(item => item.date === dateString);
  }, [calendarData, currentYear, currentMonth]);

  // Handle date click
  const handleDateClick = useCallback((date, dayData) => {
    if (dayData.length > 0) {
      setSelectedDate(date);
      setSelectedDateData(dayData);
      setModalOpen(true);
    }
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedDate(null);
    setSelectedDateData([]);
  }, []);

  // Navigation handlers
  const handlePreviousMonth = useCallback(() => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  }, [currentMonth, currentYear]);

  const handleNextMonth = useCallback(() => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  }, [currentMonth, currentYear]);

  const handleToday = useCallback(() => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  }, []);

  // Get calendar grid data
  const getCalendarGrid = useCallback(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday

    const days = [];
    const totalDays = 42; // 6 weeks * 7 days

    for (let i = 0; i < totalDays; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const isCurrentMonth = date.getMonth() === currentMonth;
      const isToday = date.toDateString() === new Date().toDateString();
      const dayNumber = date.getDate();
      const dayData = isCurrentMonth ? getDataForDate(dayNumber) : [];

      days.push({
        date: dayNumber,
        fullDate: date,
        isCurrentMonth,
        isToday,
        data: dayData
      });
    }

    return days;
  }, [currentYear, currentMonth, getDataForDate]);

  // Fetch data when component mounts or month/year changes
  useEffect(() => {
    if (isMountedRef.current && !isLoadingRef.current) {
      fetchCalendarData(currentMonth + 1, currentYear); // API expects 1-based month
    }
  }, [currentMonth, currentYear, fetchCalendarData]);

  // Component mount/unmount effect
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      isLoadingRef.current = false;
    };
  }, []);

  // Early return for loading state
  if (loading) {
    return (
      <div className="container">
        <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
          <div className="top-sec">
            <div className="card custom-card">
              <div className="card-body">
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '50vh'
                }}>
                  {loadingGif ? (
                    <img
                      src={loadingGif}
                      alt="Loading..."
                      style={{ width: '80px' }}
                    />
                  ) : (
                    <CircularProgress size={60} />
                  )}
                  <Typography variant="body1" sx={{ 
                    mt: 2, 
                    fontFamily: '"Saira", sans-serif'
                  }}>
                    Loading calendar data...
                  </Typography>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Early return for error state
  if (error && !calendarData.length) {
    return (
      <div className="container">
        <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
          <div className="top-sec">
            <div className="card custom-card">
              <div className="card-body">
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                      <strong>Error!</strong> {error}
                    </Typography>
                  </Alert>
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (!isLoadingRef.current) {
                        fetchCalendarData(currentMonth + 1, currentYear);
                      }
                    }}
                    disabled={isLoadingRef.current}
                    sx={{
                      mt: 2,
                      fontFamily: '"Saira", sans-serif',
                      background: '#ce55b9',
                      '&:hover': {
                        background: '#b8479a',
                      }
                    }}
                  >
                    Retry
                  </Button>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const calendarGrid = getCalendarGrid();
  const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const weekDaysShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const monthEvents = calendarData.filter(item => {
    const itemDate = new Date(item.date);
    return itemDate.getMonth() === currentMonth && itemDate.getFullYear() === currentYear;
  });

  return (
    <div style={{ display: 'grid', gap: '16px' }}>
      <div className="container">
        <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
          <div className="top-sec">
            <div className="card custom-card">
              <div className="card-body">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between flex-wrap pb-3">
                  <div className="d-flex align-items-center">
                    <h3 className="mb-2" style={{ color: '#ce55b9', fontWeight: 600 }}>My Planner</h3>
                  </div>
                  <CalendarToday sx={{ fontSize: 40, color: '#ce55b9', opacity: 0.6 }} />
                </div>

                {error && calendarData.length > 0 && (
                  <Alert
                    severity="warning"
                    sx={{
                      mb: 3,
                      fontFamily: '"Saira", sans-serif'
                    }} 
                    onClose={() => setError(null)}
                  >
                    <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                      {error}
                    </Typography>
                  </Alert>
                )}

                {/* Calendar Navigation */}
                <Box sx={{
                  position: 'relative',
                  mb: 4,
                  p: 0,
                  overflow: 'hidden'
                }}>
                  {/* Background gradient container */}
                  <Box sx={{
                    background: 'linear-gradient(135deg, #ce55b9 0%, #b8479a 25%, #a03d85 50%, #8a3470 75%, #742a5b 100%)',
                    borderRadius: 4,
                    p: 4,
                    position: 'relative',
                    boxShadow: '0 8px 32px rgba(206, 85, 185, 0.3)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)',
                      borderRadius: 4,
                      pointerEvents: 'none'
                    }
                  }}>
                    {/* Navigation content */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      {/* Previous button */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}>
                        <Button
                          onClick={handlePreviousMonth}
                          variant="contained"
                          sx={{
                            minWidth: 56,
                            height: 56,
                            borderRadius: 3,
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            color: 'white',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.25)',
                              border: '1px solid rgba(255, 255, 255, 0.4)',
                              transform: 'translateY(-2px) scale(1.05)',
                              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                            },
                            '&:active': {
                              transform: 'translateY(0) scale(0.98)'
                            }
                          }}
                        >
                          <ChevronLeft sx={{ fontSize: 28 }} />
                        </Button>
                        
                        {/* Decorative element */}
                        <Box sx={{
                          width: 2,
                          height: 24,
                          background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                          borderRadius: 1,
                          display: { xs: 'none', sm: 'block' }
                        }} />
                      </Box>

                      {/* Center content - Month/Year and Today button */}
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        gap: 2,
                        flex: 1,
                        mx: 3
                      }}>
                        {/* Month and Year display */}
                        <Box sx={{
                          textAlign: 'center',
                          position: 'relative'
                        }}>
                          <Typography variant="h3" sx={{
                            fontWeight: 800,
                            fontFamily: '"Saira", sans-serif',
                            color: 'white',
                            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                            lineHeight: 1.1,
                            letterSpacing: '-0.02em',
                            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            background: 'linear-gradient(45deg, #ffffff 0%, #f8f9fa 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                          }}>
                            {getMonthName(currentMonth)}
                          </Typography>
                          <Typography variant="h5" sx={{
                            fontWeight: 600,
                            fontFamily: '"Saira", sans-serif',
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                            letterSpacing: '0.05em',
                            mt: -0.5
                          }}>
                            {currentYear}
                          </Typography>
                        </Box>
                        
                        {/* Today button */}
                        <Button
                          onClick={handleToday}
                          variant="contained"
                          startIcon={<Today sx={{ fontSize: 20 }} />}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            fontFamily: '"Saira", sans-serif',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            px: 3,
                            py: 1.5,
                            borderRadius: 3,
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)',
                              border: '1px solid rgba(255, 255, 255, 0.5)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                            },
                            '&:active': {
                              transform: 'translateY(0)'
                            }
                          }}
                        >
                          Today
                        </Button>
                      </Box>

                      {/* Next button */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}>
                        {/* Decorative element */}
                        <Box sx={{
                          width: 2,
                          height: 24,
                          background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                          borderRadius: 1,
                          display: { xs: 'none', sm: 'block' }
                        }} />
                        
                        <Button
                          onClick={handleNextMonth}
                          variant="contained"
                          sx={{
                            minWidth: 56,
                            height: 56,
                            borderRadius: 3,
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            color: 'white',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.25)',
                              border: '1px solid rgba(255, 255, 255, 0.4)',
                              transform: 'translateY(-2px) scale(1.05)',
                              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                            },
                            '&:active': {
                              transform: 'translateY(0) scale(0.98)'
                            }
                          }}
                        >
                          <ChevronRight sx={{ fontSize: 28 }} />
                        </Button>
                      </Box>
                    </Box>

                    {/* Subtle decorative elements */}
                    <Box sx={{
                      position: 'absolute',
                      top: -20,
                      right: -20,
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                      pointerEvents: 'none'
                    }} />
                    <Box sx={{
                      position: 'absolute',
                      bottom: -30,
                      left: -30,
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
                      pointerEvents: 'none'
                    }} />
                  </Box>
                </Box>

                {/* Calendar Grid */}
                <Paper elevation={3} sx={{ 
                  p: 4, 
                  borderRadius: 3,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                }}>
                  {/* Calendar Container with CSS Grid */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: 2,
                    width: '100%'
                  }}>
                    {/* Week Day Headers */}
                    {weekDaysShort.map((day, index) => (
                      <Box key={day} sx={{
                        textAlign: 'center',
                        py: 2,
                        backgroundColor: 'rgba(206, 85, 185, 0.1)',
                        borderRadius: 2,
                        border: '1px solid rgba(206, 85, 185, 0.2)',
                        fontWeight: 700,
                        color: '#ce55b9',
                        fontFamily: '"Saira", sans-serif',
                        fontSize: { xs: '0.875rem', md: '1rem' }
                      }}>
                        {/* Show full names on desktop, short on mobile */}
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                          {weekDays[index]}
                        </Box>
                        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                          {day}
                        </Box>
                      </Box>
                    ))}

                    {/* Calendar Days */}
                    {calendarGrid.map((day, index) => {
                      const isClickable = day.isCurrentMonth && day.data.length > 0;
                      
                      return (
                        <Box
                          key={index}
                          sx={{
                            minHeight: { xs: 120, md: 140 },
                            p: 2,
                            cursor: isClickable ? 'pointer' : 'default',
                            backgroundColor: 
                              day.isToday ? 'rgba(206, 85, 185, 0.15)' :
                              !day.isCurrentMonth ? '#f8f9fa' :
                              day.data.length > 0 ? 'rgba(33, 150, 243, 0.05)' : 'white',
                            border: day.isToday ? '2px solid #ce55b9' : '1px solid #e0e0e0',
                            borderRadius: 2,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: day.isCurrentMonth && day.data.length > 0 ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
                            '&:hover': {
                              backgroundColor: 
                                day.isToday ? 'rgba(206, 85, 185, 0.2)' :
                                day.isCurrentMonth && day.data.length > 0 ? 'rgba(33, 150, 243, 0.1)' : 
                                !day.isCurrentMonth ? '#f8f9fa' : 'rgba(206, 85, 185, 0.05)',
                              transform: isClickable ? 'translateY(-3px) scale(1.02)' : 'none',
                              boxShadow: isClickable ? '0 8px 25px rgba(0, 0, 0, 0.15)' : 'none'
                            }
                          }}
                          onClick={() => handleDateClick(day.date, day.data)}
                        >
                          {/* Date Number */}
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            mb: 1,
                            minHeight: 32
                          }}>
                            <Typography variant="h6" sx={{
                              fontWeight: day.isToday ? 700 : day.isCurrentMonth ? 600 : 400,
                              color: day.isToday ? '#ce55b9' : day.isCurrentMonth ? '#333' : '#999',
                              fontFamily: '"Saira", sans-serif',
                              fontSize: { xs: '1rem', md: '1.125rem' }
                            }}>
                              {day.date}
                            </Typography>
                            
                            {day.data.length > 0 && (
                              <Chip
                                label={day.data.length}
                                size="small"
                                sx={{
                                  backgroundColor: '#ce55b9',
                                  color: 'white',
                                  fontFamily: '"Saira", sans-serif',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  height: 20,
                                  minWidth: 20,
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            )}
                          </Box>

                          {/* Event Indicators */}
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 0.5,
                            flex: 1,
                            overflow: 'hidden'
                          }}>
                            {day.data.slice(0, 3).map((item, itemIndex) => {
                              const typeInfo = getTypeInfo(item.type);
                              return (
                                <Tooltip 
                                  key={itemIndex} 
                                  title={`${item.name} - ${item.time}`} 
                                  arrow
                                  placement="top"
                                >
                                  <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    p: 0.5,
                                    backgroundColor: typeInfo.lightColor,
                                    borderRadius: 1,
                                    border: `1px solid ${typeInfo.color}30`,
                                    transition: 'all 0.2s ease',
                                    minHeight: 24,
                                    '&:hover': {
                                      backgroundColor: typeInfo.bgColor,
                                      transform: 'scale(1.02)'
                                    }
                                  }}>
                                    <Box sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      backgroundColor: typeInfo.dotColor,
                                      flexShrink: 0
                                    }} />
                                    <Typography variant="caption" sx={{
                                      color: typeInfo.color,
                                      fontFamily: '"Saira", sans-serif',
                                      fontWeight: 600,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      fontSize: { xs: '0.65rem', md: '0.7rem' },
                                      lineHeight: 1.2,
                                      flex: 1
                                    }}>
                                      {item.name}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              );
                            })}
                            
                            {day.data.length > 3 && (
                              <Typography variant="caption" sx={{
                                color: '#666',
                                fontFamily: '"Saira", sans-serif',
                                fontStyle: 'italic',
                                textAlign: 'center',
                                fontSize: '0.65rem',
                                mt: 0.5,
                                backgroundColor: 'rgba(206, 85, 185, 0.1)',
                                borderRadius: 1,
                                py: 0.5
                              }}>
                                +{day.data.length - 3} more
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>

                {/* Summary Stats */}
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                  <Paper elevation={2} sx={{
                    px: 4,
                    py: 2,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(206, 85, 185, 0.1) 0%, rgba(206, 85, 185, 0.05) 100%)',
                    border: '1px solid rgba(206, 85, 185, 0.2)'
                  }}>
                    <Typography variant="body1" sx={{
                      color: '#666',
                      fontFamily: '"Saira", sans-serif',
                      fontWeight: 500,
                      textAlign: 'center'
                    }}>
                      <strong style={{ color: '#ce55b9' }}>{monthEvents.length}</strong> events scheduled for {getMonthName(currentMonth)} {currentYear}
                    </Typography>
                  </Paper>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Date Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            fontFamily: '"Saira", sans-serif',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #ce55b9 0%, #b8479a 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontFamily: '"Saira", sans-serif',
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CalendarToday sx={{ fontSize: 28 }} />
            <Typography variant="h5" sx={{ 
              fontFamily: '"Saira", sans-serif',
              fontWeight: 600
            }}>
              {selectedDate && formatDate(`${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(selectedDate).padStart(2, '0')}`)}
            </Typography>
          </Box>
          <IconButton
            onClick={handleModalClose}
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4, backgroundColor: '#f8f9fa' }}>
          <Typography variant="h6" sx={{
            mb: 3,
            fontWeight: 600,
            fontFamily: '"Saira", sans-serif',
            color: '#333'
          }}>
            {selectedDateData.length} event{selectedDateData.length !== 1 ? 's' : ''} scheduled
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {selectedDateData.map((item, index) => {
              const typeInfo = getTypeInfo(item.type);
              return (
                <Card key={index} sx={{
                  borderLeft: `4px solid ${typeInfo.color}`,
                  borderRadius: 2,
                  backgroundColor: 'white',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start', 
                      mb: 2 
                    }}>
                      <Typography variant="h6" sx={{
                        fontWeight: 600,
                        fontFamily: '"Saira", sans-serif',
                        color: '#333',
                        flex: 1
                      }}>
                        {item.name}
                      </Typography>
                      
                      <Chip
                        label={typeInfo.label}
                        icon={typeInfo.icon}
                        size="medium"
                        sx={{
                          backgroundColor: typeInfo.bgColor,
                          color: typeInfo.color,
                          fontWeight: 600,
                          fontFamily: '"Saira", sans-serif',
                          border: `1px solid ${typeInfo.color}30`
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTime sx={{ fontSize: 20, color: '#666' }} />
                      <Typography variant="body1" sx={{
                        fontFamily: '"Saira", sans-serif',
                        color: '#666',
                        fontWeight: 500
                      }}>
                        {item.time}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 4, backgroundColor: 'white' }}>
          <Button
            onClick={handleModalClose}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #ce55b9 0%, #b8479a 100%)',
              fontFamily: '"Saira", sans-serif',
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 15px rgba(206, 85, 185, 0.4)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(206, 85, 185, 0.6)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Planner;